import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { isEmpty } from 'ts-utils';
import { Experiments, useExperimentSelect } from '../../../helpers/experiments';
import { PricingSelector } from '../../../store/pricing/PricingActions';
import { useAppStore } from '../../../store/Store';
import { Analytics } from '../../../util/Analytics';
import { trackPaymentAnalyticsEvent } from '../../../util/PaymentAnalytics';
import { useSmoothLoading } from '../../../util/UseSmoothLoading';
import { Checkout } from '../../payment/Checkout';
import { CheckoutInfo, PricingInfoContainer } from '../../payment/CheckoutInfo/CheckoutInfo';
import { CollectCardFormProps } from '../../payment/CollectCardForm';
import { EmailForm } from '../../payment/EmailForm';
import { Header } from '../../payment/Header/Header';
import { PatientAssistanceCta } from '../../payment/PatientAssistanceCta';
import { PaymentRedesign } from '../../payment/PaymentRedesign/PaymentRedesign';
import { cardStripeAppearance } from '../../payment/PaymentRedesign/RedesignCheckoutPanel';
import { NextButton } from '../../styles/LayoutButtons';

type Props = Pick<CollectCardFormProps, 'nextPage'>;

export const PaymentLayout: FunctionComponent<Props> = (props) => {
  const { purchase, pricing, user } = useAppStore((state) => ({
    purchase: state.purchase,
    pricing: state.pricing,
    user: state.user,
  }));

  const theme = useTheme();
  // One entry per arm, so a new arm won't compile until it's mapped here.
  const redesignLayout = useExperimentSelect(Experiments.PaymentRedesign, {
    Control: undefined,
    Program: 'program',
    Sales: 'sales',
  } as const);
  const isRedesign = redesignLayout !== undefined;

  const loading = useSmoothLoading(user.loading || purchase.intent.loading || pricing.loading);
  const showEmailForm = useRef(isEmpty(user.email)).current;
  const [showCheckout, setShowCheckout] = useState(
    purchase.payment.loading || purchase.payment.error !== undefined
  );
  // On resume routes (e.g. /start) the page can settle before geolocation has kicked off the first
  // price fetch, so wait for prices rather than tracking an event that has no price data.
  const pricesReady = useAppStore((state) => PricingSelector.prices(state) !== undefined);

  useEffect(
    function trackPaymentOpened() {
      if (!loading && pricesReady) {
        trackPaymentAnalyticsEvent(Analytics.Event.o_payment_opened);
      }
    },
    [loading, pricesReady]
  );

  const purchaseForm = (
    <>
      {showEmailForm ? (
        <EmailForm
          showButton={showCheckout}
          setShowCheckout={setShowCheckout}
          labelled={isRedesign}
        />
      ) : (
        <>
          {showCheckout || (
            <NextButton hideArrow onClick={() => setShowCheckout(true)}>
              Continue
            </NextButton>
          )}
        </>
      )}

      {showCheckout && (
        <Checkout
          nextPage={props.nextPage}
          inCard={isRedesign}
          stripeAppearance={isRedesign ? cardStripeAppearance(theme.font.primary) : undefined}
        />
      )}
    </>
  );

  return (
    <PaymentContainer data-test="payment-screen">
      <Header loading={loading} user={user} hideCoupon={isRedesign} />

      {redesignLayout !== undefined ? (
        <PaymentRedesign
          layout={redesignLayout}
          purchaseForm={purchaseForm}
          checkoutShown={showCheckout}
        />
      ) : (
        <>
          <CheckoutInfo />
          <PricingInfoContainer>{purchaseForm}</PricingInfoContainer>
          <PatientAssistanceCta />
        </>
      )}
    </PaymentContainer>
  );
};

const PaymentContainer = styled.div`
  margin: 0 -20px;
  max-width: 600px;
  margin-bottom: ${({ theme }) => theme.spacing._50px};
  display: flex;
  flex-direction: column;

  /* Checkout reads best as a compact column on wide screens. */
  @media (min-width: 600px) {
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;
  }
`;
